(() => {
'use strict';

/*
 * Matri Smart Cart v12.4 Instant
 * Stable gift/coupon/checkout build
 *
 * Fixes:
 * - Exclusive highest-milestone gift engine
 * - Prevents duplicate free gifts
 * - Gift must be quantity 1 and actual Shopify ₹0 line
 * - Fixes gift reconciler mutation-chain deadlock
 * - Correct header count excludes free gifts
 * - Checkout blocks gift-only cart / last-item race
 * - Coupon changes immediately reconcile milestone eligibility
 * - Rapid quantity changes stay optimistic but deterministic
 * - Recommendations pause during cart mutations
 * - Magic Checkout capture/guard retained
 */

const M = document.getElementById('matri-smart-cart-root');
if (!M || window.__MSC124) return;
window.__MSC124 = 1;

const R = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const P = (M.dataset.proxyRoot || '/apps/matri-smart-cart').replace(/\/$/, '');
const shop = x => R + String(x || '').replace(/^\//, '');
const proxy = x => P + '/' + String(x || '').replace(/^\//, '');
const RAW_FETCH = window.fetch.bind(window);

const S = {
  cart: null,
  cfg: null,
  offers: [],
  cartP: null,
  cfgP: null,
  giftP: null,
  proxy: true,
  opening: false,
  openingToken: 0,
  appliedCode: '',
  couponSavings: 0,
  couponMutationUntil: 0,
  mutationChain: Promise.resolve(),
  linePending: {},
  lineDebounceTimers: {},
  noticeTimer: null,
  offerSyncTimer: null,
  externalSyncTimer: null,
  lastCartReadAt: 0,
  cartReadCooldownUntil: 0,
  lastCheckoutTrackAt: 0,
  checkoutGuardBusy: false,
  checkoutReplay: false
};

const CKEY = 'msc_cfg_v124';
const CARTKEY = 'msc_cart_v124';
const CART_TTL = 45000;
const TTL = 300000;

const esc = v => String(v ?? '').replace(/[&<>"']/g, c => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;'
}[c]));

const money = cents => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0
}).format((Number(cents) || 0) / 100);

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/* ========================= REQUESTS ========================= */

function retryAfterMs(response, attempt = 0) {
  const seconds = Number.parseFloat(response?.headers?.get?.('Retry-After') || '');
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.min(5000, Math.ceil(seconds * 1000));
  }
  return Math.min(3200, 650 * (attempt + 1));
}

function requestCanRetry(url, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  if (method === 'GET' || method === 'HEAD') return true;

  let pathname = '';
  try {
    pathname = new URL(url, location.origin).pathname;
  } catch {}

  return /\/cart\/(change|update|clear)\.js$/.test(pathname);
}

async function req(url, options = {}) {
  const max429Retries = requestCanRetry(url, options) ? 2 : 0;
  let attempt = 0;

  while (true) {
    const response = await RAW_FETCH(url, options);
    const text = await response.text();

    if (response.status === 429 && attempt < max429Retries) {
      const wait = retryAfterMs(response, attempt);
      S.cartReadCooldownUntil = Math.max(S.cartReadCooldownUntil, Date.now() + wait);
      attempt++;
      await sleep(wait);
      continue;
    }

    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      const error = new Error(
        response.status === 429
          ? 'Shopify is briefly rate limiting cart requests.'
          : `Invalid Smart Cart response (HTTP ${response.status}).`
      );
      error.status = response.status;
      error.retryAfter = response.status === 429 ? retryAfterMs(response, attempt) : 0;
      throw error;
    }

    if (!response.ok || data?.ok === false) {
      const error = new Error(
        data?.error ||
        data?.description ||
        data?.message ||
        (response.status === 429
          ? 'Shopify is briefly rate limiting cart requests.'
          : `Shopify request failed (HTTP ${response.status}).`)
      );
      error.status = response.status;
      error.retryAfter = response.status === 429 ? retryAfterMs(response, attempt) : 0;
      throw error;
    }

    return data;
  }
}

function enqueueMutation(task) {
  const run = S.mutationChain.catch(() => {}).then(task);
  S.mutationChain = run.catch(() => {});
  return run;
}

/* ========================= ANALYTICS ========================= */

const sid = (() => {
  try {
    let value = localStorage.getItem('msc_sid');
    if (!value) {
      value = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('msc_sid', value);
    }
    return value;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
})();

function track(event_name, payload = {}) {
  try {
    RAW_FETCH(proxy('track'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_name,
        session_id: sid,
        cart_token: S.cart?.token || '',
        ...payload
      })
    }).catch(() => {});
  } catch {}
}

function trackCheckoutIntent(source = 'unknown') {
  const now = Date.now();
  if (now - Number(S.lastCheckoutTrackAt || 0) < 1800) return;

  S.lastCheckoutTrackAt = now;

  track('matri_checkout_clicked', {
    value_cents: Number(S.cart?.total_price || 0),
    coupon_code: couponCodeFromCart(),
    metadata: {
      item_count: paidItemCount(),
      source,
      magic_checkout_compatible_capture: true
    }
  });
}

/* ========================= SHELL ========================= */

function shell() {
  if (document.getElementById('msc-drawer')) return;

  document.body.insertAdjacentHTML('beforeend', `
    <div id="msc-overlay"></div>
    <aside id="msc-drawer" aria-hidden="true">
      <header class="msc-head">
        <div>
          <small>MATRI</small>
          <div>
            <h3>Your Bag</h3>
            <span id="msc-count">0</span>
          </div>
        </div>
        <button type="button" class="msc-close" aria-label="Close cart">×</button>
      </header>

      <main class="msc-scroll">
        <div id="msc-body">
          <div data-msc-part="notice"></div>
          <div data-msc-part="progress"></div>
          <div data-msc-part="items"></div>
          <div data-msc-part="offers"></div>
          <div data-msc-part="coupons"></div>
          <div data-msc-part="trust"></div>
        </div>
      </main>

      <footer id="msc-foot"></footer>
    </aside>
  `);

  document.getElementById('msc-overlay')?.addEventListener('click', close);
  document.querySelector('.msc-close')?.addEventListener('click', close);
}

function patchNode(node, html) {
  if (!node || node.__mscHtml === html) return false;
  node.__mscHtml = html;
  node.innerHTML = html;
  return true;
}

function patchPart(key, html) {
  return patchNode(
    document.querySelector(`#msc-body [data-msc-part="${key}"]`),
    html
  );
}

/* ========================= CONFIG ========================= */

function cacheCfg(data) {
  try {
    sessionStorage.setItem(CKEY, JSON.stringify({ t: Date.now(), d: data }));
  } catch {}
}

function cachedCfg() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CKEY) || 'null');
    return cached &&
      cached.d &&
      Date.now() - Number(cached.t || 0) < TTL
      ? cached.d
      : null;
  } catch {
    return null;
  }
}

function cacheCartSnapshot(data) {
  if (!data || !Array.isArray(data.items)) return;

  try {
    sessionStorage.setItem(
      CARTKEY,
      JSON.stringify({
        t: Date.now(),
        d: data
      })
    );
  } catch {}
}

function hydrateCartSnapshot() {
  if (S.cart) return S.cart;

  try {
    const cached = JSON.parse(
      sessionStorage.getItem(CARTKEY) || 'null'
    );

    if (
      !cached ||
      !cached.d ||
      !Array.isArray(cached.d.items) ||
      Date.now() - Number(cached.t || 0) > CART_TTL
    ) {
      return null;
    }

    S.cart = cached.d;
    S.lastCartReadAt = Number(cached.t || 0);
    syncCouponStateFromCart();

    return S.cart;
  } catch {
    return null;
  }
}

function applySettings() {
  const settings = S.cfg?.settings || {};

  const vars = {
    '--a': settings.accent_color || '#8E7AB5',
    '--w': `${Math.min(395, Math.max(350, Number(settings.cart_width) || 380))}px`,
    '--cbg': settings.coupon_bg_color || '#F7F3FB',
    '--ct': settings.coupon_text_color || '#5F467F',
    '--cb': settings.coupon_border_color || '#D8CCE8',
    '--cbtn': settings.coupon_button_color || settings.accent_color || '#8E7AB5',
    '--cbt': settings.coupon_button_text_color || '#FFFFFF',
    '--msc-bg': settings.drawer_bg_color || '#FFFFFF',
    '--msc-text': settings.drawer_text_color || '#211D2B',
    '--msc-success': settings.success_color || '#17784D',
    '--msc-border': settings.border_color || '#ECE8F0',
    '--msc-card-radius': `${Number(settings.card_radius || 12)}px`,
    '--msc-button-radius': `${Number(settings.button_radius || 10)}px`,
    '--msc-checkout-bg': settings.checkout_bg_color || settings.accent_color || '#8E7AB5',
    '--msc-checkout-text': settings.checkout_text_color || '#FFFFFF'
  };

  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });

  const title = document.querySelector('#msc-drawer h3');
  if (title) title.textContent = settings.cart_title || 'Your Bag';
}

async function cfg(force = false) {
  if (!force && S.cfg) return S.cfg;
  if (!force && S.cfgP) return S.cfgP;

  if (!force) {
    const cached = cachedCfg();
    if (cached) {
      S.cfg = cached;
      S.proxy = true;
      applySettings();
      return cached;
    }
  }

  S.cfgP = req(proxy('config'), {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  })
    .then(data => {
      S.cfg = data;
      S.proxy = true;
      cacheCfg(data);
      applySettings();
      return data;
    })
    .catch(error => {
      S.proxy = false;
      throw error;
    })
    .finally(() => {
      S.cfgP = null;
    });

  return S.cfgP;
}

/* ========================= COUPON HELPERS ========================= */

function normalizeDiscountType(app) {
  return String(app?.type || app?.application_type || '').trim().toLowerCase();
}

function discountApplicationCode(app) {
  return String(app?.code || app?.title || app?.key || '').trim();
}

function lineDiscountApplications() {
  const apps = [];

  for (const item of S.cart?.items || []) {
    const allocations = [
      ...(Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : []),
      ...(Array.isArray(item.discount_allocations)
        ? item.discount_allocations
        : [])
    ];

    for (const allocation of allocations) {
      const app = allocation?.discount_application || allocation?.application;
      if (app) apps.push(app);
    }
  }

  return apps;
}

function allDiscountApplications() {
  const raw = [
    ...(Array.isArray(S.cart?.cart_level_discount_applications)
      ? S.cart.cart_level_discount_applications
      : []),
    ...(Array.isArray(S.cart?.discount_applications)
      ? S.cart.discount_applications
      : []),
    ...lineDiscountApplications()
  ];

  const seen = new Set();

  return raw.filter(app => {
    const key = [
      normalizeDiscountType(app),
      String(app?.code || '').trim().toUpperCase(),
      String(app?.title || '').trim().toUpperCase(),
      String(app?.key || '').trim().toUpperCase()
    ].join('|');

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isDiscountCodeApplication(app, expectedCode = '') {
  if (!app) return false;

  const type = normalizeDiscountType(app);
  const wanted = String(expectedCode || '').trim().toUpperCase();

  const candidates = [
    app?.code,
    app?.title,
    app?.key
  ]
    .map(value => String(value || '').trim().toUpperCase())
    .filter(Boolean);

  if (wanted) return candidates.includes(wanted);

  return type === 'discount_code' || type === 'code';
}

function currentCouponApplication(expectedCode = '') {
  const apps = allDiscountApplications();
  const wanted = String(expectedCode || '').trim().toUpperCase();

  return wanted
    ? (apps.find(app => isDiscountCodeApplication(app, wanted)) || null)
    : (apps.find(app => isDiscountCodeApplication(app)) || null);
}

function couponCodeFromCart() {
  const app = currentCouponApplication(S.appliedCode);

  return app
    ? String(app.code || app.title || app.key || S.appliedCode || '').trim()
    : String(S.appliedCode || '').trim();
}

function couponDiscountFromAllocations(expectedCode = '') {
  let total = 0;

  for (const item of S.cart?.items || []) {
    const allocations = [
      ...(Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : []),
      ...(Array.isArray(item.discount_allocations)
        ? item.discount_allocations
        : [])
    ];

    for (const allocation of allocations) {
      const app = allocation?.discount_application || allocation?.application || null;

      if (!isDiscountCodeApplication(app, expectedCode)) continue;

      total += Number(
        allocation?.amount ||
        allocation?.discounted_amount ||
        0
      );
    }
  }

  return Math.max(0, total);
}

function directCouponApplicationAmount(app) {
  if (!app) return 0;

  return Math.max(
    0,
    Number(
      app.total_allocated_amount ||
      app.allocated_amount ||
      app.amount ||
      0
    )
  );
}

function currentCouponDiscount() {
  const app = currentCouponApplication(S.appliedCode);

  if (!app && !S.appliedCode) return 0;

  const direct = directCouponApplicationAmount(app);
  if (direct > 0) return direct;

  const allocated = couponDiscountFromAllocations(couponCodeFromCart());
  if (allocated > 0) return allocated;

  return Math.max(0, Number(S.couponSavings || 0));
}

function syncCouponStateFromCart() {
  const preferred = String(S.appliedCode || '').trim();

  const app =
    currentCouponApplication(preferred) ||
    currentCouponApplication('');

  if (!app) {
    if (Date.now() > Number(S.couponMutationUntil || 0)) {
      S.appliedCode = '';
      S.couponSavings = 0;
    }
    return;
  }

  const code = discountApplicationCode(app);
  if (code) S.appliedCode = code.toUpperCase();

  const direct = directCouponApplicationAmount(app);
  const allocated = couponDiscountFromAllocations(S.appliedCode);

  if (direct > 0 || allocated > 0) {
    S.couponSavings = Math.max(direct, allocated);
  }
}

function cartHasCouponCode(code = '') {
  return !!currentCouponApplication(code);
}

/* ========================= CART ========================= */

async function cart(force = false) {
  if (S.cartP) return S.cartP;

  const now = Date.now();

  if (!force && S.cart && now - Number(S.lastCartReadAt || 0) < 500) {
    return S.cart;
  }

  const cooldown = Math.max(
    0,
    Number(S.cartReadCooldownUntil || 0) - now
  );

  if (cooldown > 0) {
    await sleep(cooldown);
  }

  S.cartP = req(shop('cart.js'), {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  })
    .then(data => {
      S.cart = data;
      S.lastCartReadAt = Date.now();
      cacheCartSnapshot(data);
      syncCouponStateFromCart();
      return data;
    })
    .catch(error => {
      if (Number(error?.status || 0) === 429 && S.cart) {
        S.cartReadCooldownUntil = Math.max(
          S.cartReadCooldownUntil,
          Date.now() + Number(error?.retryAfter || 1200)
        );
        return S.cart;
      }

      throw error;
    })
    .finally(() => {
      S.cartP = null;
    });

  return S.cartP;
}

function adoptCart(data) {
  if (data && Array.isArray(data.items)) {
    S.cart = data;
    S.lastCartReadAt = Date.now();
    cacheCartSnapshot(data);
    syncCouponStateFromCart();
    return true;
  }

  return false;
}

const empty = () => (
  !S.cart ||
  Number(S.cart.item_count || 0) === 0 ||
  !Array.isArray(S.cart.items) ||
  !S.cart.items.length
);

/* ========================= PAID CART SAFETY ========================= */

function paidMerchandiseLines() {
  return (S.cart?.items || []).filter(
    item =>
      !isGiftLine(item) &&
      Number(item.quantity || 0) > 0
  );
}

function hasPaidMerchandise() {
  return paidMerchandiseLines().length > 0;
}

function customerCartEmpty() {
  return !hasPaidMerchandise();
}

function pendingLineMutationCount() {
  return Object.keys(S.linePending || {}).length;
}

/* ========================= HEADER COUNT ========================= */

function paidItemCount() {
  return (S.cart?.items || []).reduce((sum, item) => {
    if (isGiftLine(item)) return sum;

    return sum + Math.max(
      0,
      Number(item.quantity || 0)
    );
  }, 0);
}

function syncThemeCartCount({ emitEvents = true } = {}) {
  const count = paidItemCount();

  const own = document.getElementById('msc-count');
  if (own) own.textContent = String(count);

  const selectors = [
    '.m-cart-count',
    '.m-header__cart-count',
    '.m-cart-count-bubble',
    '.cart-count-bubble',
    '.cart-count-number',
    '.header__cart-count',
    '[data-cart-count]',
    '[data-cart-item-count]',
    '[data-cart-items-count]',
    '[data-cart-count-number]'
  ];

  const nodes = new Set();

  selectors.forEach(selector => {
    try {
      document.querySelectorAll(selector).forEach(node => nodes.add(node));
    } catch {}
  });

  nodes.forEach(node => {
    if (!(node instanceof Element)) return;

    const nested = node.querySelector(
      '[data-cart-count-number], .cart-count-number, .m-cart-count-number'
    );

    if (nested) nested.textContent = String(count);
    else node.textContent = String(count);

    [
      'data-cart-count',
      'data-cart-item-count',
      'data-cart-items-count',
      'data-cart-count-number'
    ].forEach(attr => {
      if (node.hasAttribute(attr)) {
        node.setAttribute(attr, String(count));
      }
    });

    node.classList.toggle('is-empty', count === 0);

    node.setAttribute(
      'aria-label',
      `${count} item${count === 1 ? '' : 's'} in cart`
    );
  });

  if (!emitEvents) return;

  const detail = {
    cart: S.cart,
    item_count: count,
    source: 'matri-smart-cart'
  };

  [
    'cart:updated',
    'cart:refresh',
    'cart:change'
  ].forEach(name => {
    try {
      document.dispatchEvent(
        new CustomEvent(name, { detail })
      );
    } catch {}
  });
}

/* ========================= GIFTS ========================= */

function isGiftLine(item) {
  return !!(
    item?.properties &&
    item.properties._matri_free_gift !== undefined &&
    item.properties._matri_free_gift !== null &&
    String(item.properties._matri_free_gift) !== ''
  );
}

function giftRuleById(id) {
  return (
    Array.isArray(S.cfg?.gifts)
      ? S.cfg.gifts
      : []
  ).find(
    rule => Number(rule.id) === Number(id)
  ) || null;
}

function giftRuleForLine(item) {
  const id = item?.properties?._matri_free_gift;

  return (
    id === undefined ||
    id === null ||
    String(id) === ''
  )
    ? null
    : giftRuleById(id);
}

function markedGiftLines() {
  return (S.cart?.items || []).filter(isGiftLine);
}

function giftLineForRule(rule) {
  if (!rule || !S.cart?.items?.length) return null;

  return S.cart.items.find(item =>
    Number(item.variant_id) === Number(rule.gift_variant_id) &&
    String(item.properties?._matri_free_gift || '') === String(rule.id)
  ) || null;
}

function giftLineIsFree(line) {
  return !!(
    line &&
    Number(
      line.final_line_price ??
      line.line_price ??
      0
    ) === 0
  );
}

/*
 * Payable merchandise value AFTER coupons / automatic discounts.
 * Marked gift value is removed defensively so a bad gift variant
 * can never qualify itself for a milestone.
 */
function qualifyingCartTotal() {
  const total = Math.max(
    0,
    Number(S.cart?.total_price || 0)
  );

  const markedGiftValue = (S.cart?.items || []).reduce(
    (sum, item) => {
      if (!isGiftLine(item)) return sum;

      return sum + Math.max(
        0,
        Number(
          item.final_line_price ??
          item.line_price ??
          0
        )
      );
    },
    0
  );

  return Math.max(
    0,
    total - markedGiftValue
  );
}

function paidCartTotal() {
  return qualifyingCartTotal();
}

function enabledGiftRules() {
  return (
    Array.isArray(S.cfg?.gifts)
      ? S.cfg.gifts
      : []
  )
    .filter(rule =>
      Number(rule.enabled ?? 1) === 1 &&
      Number(rule.auto_add ?? 1) === 1 &&
      Number(rule.threshold_cents || 0) > 0 &&
      Number(rule.gift_variant_id || 0) > 0
    )
    .sort((a, b) => {
      const thresholdDiff =
        Number(a.threshold_cents || 0) -
        Number(b.threshold_cents || 0);

      if (thresholdDiff !== 0) {
        return thresholdDiff;
      }

      return (
        Number(a.sort_order || 0) -
        Number(b.sort_order || 0)
      );
    });
}

/*
 * EXCLUSIVE milestone gift.
 * Only the highest currently qualified rule is desired.
 */
function desiredGiftRules() {
  const total = qualifyingCartTotal();
  const rules = enabledGiftRules();

  if (!rules.length) return [];

  const qualified = rules.filter(
    rule =>
      total >= Number(rule.threshold_cents || 0)
  );

  if (!qualified.length) return [];

  const highest = qualified.reduce(
    (best, rule) => {
      if (!best) return rule;

      return (
        Number(rule.threshold_cents || 0) >=
        Number(best.threshold_cents || 0)
      )
        ? rule
        : best;
    },
    null
  );

  return highest ? [highest] : [];
}

/* ========================= RAW CART MUTATIONS ========================= */

async function changeLineRaw(key, quantity) {
  const data = await req(shop('cart/change.js'), {
    method: 'POST',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify({
      id: key,
      quantity: Number(quantity)
    })
  });

  if (!adoptCart(data)) {
    await cart(true);
  }

  syncThemeCartCount({
    emitEvents: false
  });

  return S.cart;
}

async function addGiftVariantRaw(rule) {
  try {
    await req(shop('cart/add.js'), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify({
        items: [{
          id: Number(rule.gift_variant_id),
          quantity: 1,
          properties: {
            _matri_free_gift: String(rule.id),
            _matri_free_gift_label: 'FREE GIFT',
            _matri_smart_cart: '1'
          }
        }]
      })
    });
  } catch (error) {
    if (Number(error?.status || 0) !== 429) {
      throw error;
    }

    await sleep(
      Number(error?.retryAfter || 1200)
    );

    await cart(true);

    if (giftLineForRule(rule)) {
      return S.cart;
    }

    throw error;
  }

  await sleep(55);
  await cart(true);

  syncThemeCartCount({
    emitEvents: false
  });

  return S.cart;
}

/*
 * IMPORTANT:
 * Gift mutations here are DIRECT.
 * They must NOT enqueue into mutationChain because syncFreeGifts()
 * can be called from inside an already queued cart mutation.
 */
async function ensureGiftRule(rule) {
  if (!rule) return false;

  let line = giftLineForRule(rule);

  if (line) {
    if (!giftLineIsFree(line)) {
      await changeLineRaw(line.key, 0);

      throw new Error(
        `Free gift rule ${rule.id} is not using a Shopify ₹0 variant.`
      );
    }

    if (Number(line.quantity || 0) !== 1) {
      await changeLineRaw(line.key, 1);

      line = giftLineForRule(rule);
    }

    return false;
  }

  let lastError = null;

  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await addGiftVariantRaw(rule);

      line = giftLineForRule(rule);

      if (!line) {
        await sleep(100 + attempt * 100);
        await cart(true);
        line = giftLineForRule(rule);
      }

      if (!line) {
        continue;
      }

      if (!giftLineIsFree(line)) {
        await changeLineRaw(line.key, 0);

        throw new Error(
          `Free gift rule ${rule.id} is not using a Shopify ₹0 variant.`
        );
      }

      if (Number(line.quantity || 0) !== 1) {
        await changeLineRaw(line.key, 1);
      }

      track('matri_free_gift_added', {
        variant_id: Number(rule.gift_variant_id),
        value_cents: 0,
        metadata: {
          gift_rule_id: Number(rule.id),
          threshold_cents: Number(rule.threshold_cents || 0),
          qualifying_cart_cents: qualifyingCartTotal(),
          engine: 'exclusive_highest_milestone_zero_price_variant',
          auto_added: true,
          ajax: true,
          attempt: attempt + 1
        }
      });

      return true;

    } catch (error) {
      lastError = error;

      if (
        /not using a Shopify ₹0 variant/i.test(
          String(error?.message || '')
        )
      ) {
        throw error;
      }

      if (attempt < 2) {
        await sleep(
          Number(error?.status || 0) === 429
            ? 600 + attempt * 400
            : 180 + attempt * 180
        );
      }
    }
  }

  if (lastError) {
    throw lastError;
  }

  return false;
}

/*
 * Stable, direct, idempotent gift reconciler.
 *
 * No enqueueMutation() is used inside this function.
 * This avoids the deadlock/race where a mutation waits for gift sync
 * while gift sync waits for another queued mutation.
 */
async function syncFreeGifts() {
  if (!S.cart || !S.cfg) return false;
  if (S.giftP) return S.giftP;

  S.giftP = (async () => {
    let changed = false;

    /*
     * SPEED NOTE:
     * The caller already owns the latest Shopify cart state.
     * changeLineRaw() returns/adopts a complete cart, while
     * addGiftVariantRaw() performs its own single authoritative read.
     * Therefore we do NOT repeatedly call /cart.js here.
     */
    const total = qualifyingCartTotal();
    const rules = enabledGiftRules();

    const qualifiedRules = rules.filter(
      rule => total >= Number(rule.threshold_cents || 0)
    );

    const desiredRule = qualifiedRules.length
      ? qualifiedRules[qualifiedRules.length - 1]
      : null;

    const desiredRuleId = desiredRule
      ? String(desiredRule.id)
      : '';

    /* Remove stale / invalid / duplicate gift lines. */
    let keeperFound = false;

    for (const snapshotLine of [...markedGiftLines()]) {
      const currentLine = (S.cart?.items || []).find(
        item => String(item.key) === String(snapshotLine.key)
      );

      if (!currentLine) continue;

      const lineRuleId = String(
        currentLine.properties?._matri_free_gift || ''
      );

      const correctRule =
        !!desiredRule &&
        lineRuleId === desiredRuleId;

      const correctVariant =
        correctRule &&
        Number(currentLine.variant_id || 0) ===
          Number(desiredRule.gift_variant_id || 0);

      const validKeeper =
        correctRule &&
        correctVariant &&
        giftLineIsFree(currentLine) &&
        !keeperFound;

      if (!validKeeper) {
        await changeLineRaw(currentLine.key, 0);
        changed = true;
        continue;
      }

      keeperFound = true;

      if (Number(currentLine.quantity || 0) !== 1) {
        await changeLineRaw(currentLine.key, 1);
        changed = true;
      }
    }

    /* No gift milestone reached => gift-free cart. */
    if (!desiredRule) {
      for (const snapshotLine of [...markedGiftLines()]) {
        const currentLine = (S.cart?.items || []).find(
          item => String(item.key) === String(snapshotLine.key)
        );

        if (!currentLine) continue;

        await changeLineRaw(currentLine.key, 0);
        changed = true;
      }

      return changed;
    }

    /* Add the highest qualified gift when missing. */
    let desiredLine = giftLineForRule(desiredRule);

    if (!desiredLine) {
      try {
        const added = await ensureGiftRule(desiredRule);
        if (added) changed = true;
      } catch (error) {
        console.error('Matri Smart Cart: FREE GIFT ADD FAILED', {
          rule_id: desiredRule.id,
          variant_id: desiredRule.gift_variant_id,
          threshold: desiredRule.threshold_cents,
          qualifying_total: total,
          error
        });

        if (/₹0 variant/i.test(String(error?.message || ''))) {
          notice('Free gift configuration needs a Shopify ₹0 variant.');
        } else {
          notice('Your free gift could not be added. Please refresh the cart.');
        }

        throw error;
      }
    }

    desiredLine = giftLineForRule(desiredRule);

    /* One controlled retry only if Shopify did not expose the added line. */
    if (!desiredLine) {
      await sleep(70);
      await addGiftVariantRaw(desiredRule);
      changed = true;
      desiredLine = giftLineForRule(desiredRule);
    }

    if (!desiredLine) {
      throw new Error(
        `Free gift could not be added for rule ${desiredRule.id}.`
      );
    }

    if (!giftLineIsFree(desiredLine)) {
      await changeLineRaw(desiredLine.key, 0);

      throw new Error(
        `Free gift rule ${desiredRule.id} is not using a Shopify ₹0 variant.`
      );
    }

    if (Number(desiredLine.quantity || 0) !== 1) {
      await changeLineRaw(desiredLine.key, 1);
      changed = true;
    }

    /* Final in-memory duplicate sanitizer. */
    let keptDesired = false;

    for (const snapshotLine of [...markedGiftLines()]) {
      const line = (S.cart?.items || []).find(
        item => String(item.key) === String(snapshotLine.key)
      );

      if (!line) continue;

      const valid =
        String(line.properties?._matri_free_gift || '') ===
          String(desiredRule.id) &&
        Number(line.variant_id || 0) ===
          Number(desiredRule.gift_variant_id || 0) &&
        giftLineIsFree(line) &&
        Number(line.quantity || 0) === 1 &&
        !keptDesired;

      if (valid) {
        keptDesired = true;
        continue;
      }

      await changeLineRaw(line.key, 0);
      changed = true;
    }

    const finalDesired = giftLineForRule(desiredRule);
    const allMarked = markedGiftLines();

    if (
      !finalDesired ||
      allMarked.length !== 1 ||
      !giftLineIsFree(finalDesired) ||
      Number(finalDesired.quantity || 0) !== 1
    ) {
      throw new Error(
        `Gift reconciliation failed for milestone rule ${desiredRule.id}.`
      );
    }

    return changed;
  })();

  try {
    return await S.giftP;
  } finally {
    S.giftP = null;
  }
}

/* ========================= OFFERS ========================= */

function offerAllowed(offer) {
  const variantId = Number(offer?.variant_id || 0);

  if (!variantId || Number(offer?.price_cents || 0) <= 0) {
    return false;
  }

  if (
    enabledGiftRules().some(
      rule =>
        Number(rule.gift_variant_id) === variantId
    )
  ) {
    return false;
  }

  if (
    (S.cart?.items || []).some(
      item =>
        Number(item.variant_id) === variantId
    )
  ) {
    return false;
  }

  return true;
}

async function offers() {
  if (customerCartEmpty() || !S.proxy) {
    S.offers = [];
    return [];
  }

  try {
    const data = await req(proxy('recommendations'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        cart_total_cents: qualifyingCartTotal(),
        variant_ids: S.cart.items.map(
          item => Number(item.variant_id)
        )
      })
    });

    const seen = new Set();

    S.offers = (
      Array.isArray(data.offers)
        ? data.offers
        : []
    ).filter(offer => {
      const key = String(offer.variant_id || '');

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);

      return offerAllowed(offer);
    });

  } catch {
    S.offers = [];
  }

  return S.offers;
}

function scheduleOffers(delay = 150) {
  clearTimeout(S.offerSyncTimer);

  S.offerSyncTimer = setTimeout(async () => {
    if (
      S.giftP ||
      pendingLineMutationCount() > 0 ||
      Date.now() < Number(S.couponMutationUntil || 0)
    ) {
      scheduleOffers(140);
      return;
    }

    if (customerCartEmpty()) {
      S.offers = [];
      renderOffers();
      return;
    }

    await offers();
    renderOffers();
  }, delay);
}


/* ========================= EFFECTIVE MILESTONES ========================= */

/*
 * Free Gift Rules are the source of truth for gift thresholds.
 *
 * The admin has separate "Milestones" and "Free Gifts" sections.
 * If their thresholds differ, the old UI could say:
 *
 *   "₹101 away from FREE Toilet Seat Sanitizer"
 *
 * while the sanitizer was already correctly in the cart.
 *
 * We preserve normal milestones such as FREE Shipping, but when a
 * milestone clearly describes one of the configured gift rules,
 * its threshold is replaced by the Free Gift Rule threshold.
 */

function milestoneWords(value) {
  const stop = new Set([
    'free', 'gift', 'matri', 'the', 'a', 'an', 'with',
    'your', 'order', 'reward', 'rewards'
  ]);

  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(word => word.length >= 3 && !stop.has(word));
}

function giftRuleLabel(rule) {
  return String(
    rule?.name ||
    rule?.title ||
    rule?.gift_title ||
    rule?.product_title ||
    rule?.label ||
    ''
  ).trim();
}

function milestoneGiftMatchScore(milestone, rule) {
  const a = milestoneWords(
    milestone?.title ||
    milestone?.unlocked_text ||
    ''
  );

  const b = milestoneWords(
    giftRuleLabel(rule)
  );

  if (!a.length || !b.length) return 0;

  const bs = new Set(b);
  const matches = a.filter(word => bs.has(word)).length;

  if (!matches) return 0;

  /*
   * Require either two meaningful matching words, or one word
   * when both labels are very short. This avoids "FREE Shipping"
   * accidentally matching a gift.
   */
  if (matches >= 2) {
    return matches / Math.max(a.length, b.length);
  }

  if (a.length <= 2 && b.length <= 2) {
    return 0.51;
  }

  return 0;
}

function effectiveMilestones() {
  const base = Array.isArray(S.cfg?.milestones)
    ? S.cfg.milestones.map(item => ({ ...item }))
    : [];

  const gifts = enabledGiftRules();
  const matchedGiftIds = new Set();

  const merged = base.map(milestone => {
    let bestRule = null;
    let bestScore = 0;

    for (const rule of gifts) {
      const score = milestoneGiftMatchScore(milestone, rule);

      if (score > bestScore) {
        bestScore = score;
        bestRule = rule;
      }
    }

    if (!bestRule || bestScore <= 0) {
      return milestone;
    }

    matchedGiftIds.add(String(bestRule.id));

    return {
      ...milestone,

      /*
       * Gift rule wins. This keeps UI and actual gift engine identical.
       */
      threshold_cents: Number(bestRule.threshold_cents || 0),

      _msc_gift_rule_id: Number(bestRule.id),
      _msc_threshold_source: 'free_gift_rule'
    };
  });

  /*
   * If a gift exists in Free Gift Rules but there is no corresponding
   * milestone row, still show it in the progress UI.
   */
  for (const rule of gifts) {
    if (matchedGiftIds.has(String(rule.id))) continue;

    const label =
      giftRuleLabel(rule) ||
      `Gift ${rule.id}`;

    merged.push({
      id: `gift-${rule.id}`,
      title: /^free\b/i.test(label)
        ? label
        : `FREE ${label}`,
      unlocked_text: `${label} unlocked`,
      threshold_cents: Number(rule.threshold_cents || 0),
      _msc_gift_rule_id: Number(rule.id),
      _msc_threshold_source: 'free_gift_rule'
    });
  }

  /*
   * Deduplicate accidental repeated rows after merging.
   */
  const seen = new Set();

  return merged
    .filter(item => Number(item.threshold_cents || 0) > 0)
    .sort(
      (a, b) =>
        Number(a.threshold_cents || 0) -
        Number(b.threshold_cents || 0)
    )
    .filter(item => {
      const key = [
        Number(item.threshold_cents || 0),
        String(item.title || '').trim().toLowerCase()
      ].join('|');

      if (seen.has(key)) return false;

      seen.add(key);
      return true;
    });
}

/* ========================= UI ========================= */

function progress() {
  if (customerCartEmpty()) return '';

  const milestones = effectiveMilestones();

  if (
    S.cfg?.settings?.enable_milestones == 0 ||
    !milestones.length
  ) {
    return '';
  }

  /*
   * Same qualifying total used by the gift engine.
   * This removes UI/gift eligibility disagreement.
   */
  const total = qualifyingCartTotal();

  const sorted = [...milestones].sort(
    (a, b) =>
      Number(a.threshold_cents || 0) -
      Number(b.threshold_cents || 0)
  );

  const next = sorted.find(
    milestone =>
      total < Number(milestone.threshold_cents || 0)
  );

  const max = Number(
    sorted.at(-1)?.threshold_cents || 1
  );

  const percentage = Math.min(
    100,
    Math.round(total / max * 100)
  );

  return `
    <section class="msc-progress">
      <span>🎁</span>
      <div>
        <div class="top">
          <b>${
            next
              ? `${money(
                  Math.max(
                    0,
                    Number(next.threshold_cents || 0) - total
                  )
                )} away from ${esc(next.title)}`
              : esc(
                  sorted.at(-1)?.unlocked_text ||
                  'All rewards unlocked'
                )
          }</b>

          <em>${percentage}%</em>
        </div>

        <div class="bar">
          <i style="width:${percentage}%"></i>
        </div>

        <div class="rewards">
          ${
            sorted.map(milestone => {
              const on =
                total >= Number(milestone.threshold_cents || 0);

              return `
                <small class="${on ? 'on' : ''}">
                  ${on ? '✓ ' : ''}${esc(milestone.title)}
                </small>
              `;
            }).join('')
          }
        </div>
      </div>
    </section>
  `;
}

function cartLineByKey(key) {
  return (S.cart?.items || []).find(
    item =>
      String(item.key) === String(key)
  ) || null;
}

function items() {
  if (empty()) {
    return `
      <div class="msc-empty">
        <div>♡</div>
        <b>Your bag is empty</b>
        <span>Add a product to see offers and rewards.</span>
        <button id="msc-continue" type="button">
          Continue shopping
        </button>
      </div>
    `;
  }

  return `
    <section class="msc-items">
      ${
        S.cart.items.map(item => {
          const isGift = !!giftRuleForLine(item);

          const original = Number(
            item.original_line_price ??
            item.line_price ??
            item.final_line_price ??
            0
          );

          const final = Number(
            item.final_line_price ??
            item.line_price ??
            0
          );

          const saving = Math.max(
            0,
            original - final
          );

          const pct =
            original > 0 &&
            saving > 0
              ? Math.round(saving / original * 100)
              : 0;

          const key = String(item.key || '');

          return `
            <article
              class="msc-item ${isGift ? 'msc-cart-gift' : ''}"
              data-line-key="${esc(key)}"
            >
              ${
                item.image
                  ? `
                    <img
                      src="${esc(item.image)}"
                      alt="${esc(item.product_title)}"
                      loading="lazy"
                      decoding="async"
                    >
                  `
                  : `
                    <div class="msc-item-placeholder">♡</div>
                  `
              }

              <div class="msc-item-main">
                ${
                  isGift
                    ? `<small class="msc-free-pill">🎁 FREE GIFT</small>`
                    : ''
                }

                <b class="msc-item-title">
                  ${esc(item.product_title)}
                </b>

                ${
                  item.variant_title &&
                  item.variant_title !== 'Default Title'
                    ? `
                      <small class="msc-item-variant">
                        ${esc(item.variant_title)}
                      </small>
                    `
                    : ''
                }

                ${
                  isGift
                    ? `
                      <div class="msc-gift-line-note">
                        🎁 Free with your order
                      </div>
                    `
                    : `
                      <div class="msc-item-actions">
                        <div class="qty">
                          <button
                            type="button"
                            data-key="${esc(key)}"
                            data-q="${Math.max(0, Number(item.quantity) - 1)}"
                          >−</button>

                          <span>${item.quantity}</span>

                          <button
                            type="button"
                            data-key="${esc(key)}"
                            data-q="${Number(item.quantity) + 1}"
                          >+</button>
                        </div>

                        <button
                          type="button"
                          class="msc-delete-item"
                          data-key="${esc(key)}"
                          data-q="0"
                          aria-label="Remove ${esc(item.product_title)}"
                        >
                          <svg viewBox="0 0 24 24" width="17" height="17">
                            <path
                              fill="currentColor"
                              d="M9 3h6l1 2h4v2h-1l-1 14H6L5 7H4V5h4l1-2Zm-1.99 4 .86 12h8.26l.86-12H7.01ZM10 9h2v8h-2V9Zm4 0h2v8h-2V9Z"
                            />
                          </svg>
                        </button>
                      </div>
                    `
                }
              </div>

              <aside class="msc-item-price">
                ${
                  isGift && giftLineIsFree(item)
                    ? `
                      ${original > 0 ? `<del>${money(original)}</del>` : ''}
                      <strong class="msc-free-price">FREE</strong>
                    `
                    : `
                      ${saving > 0 ? `<del>${money(original)}</del>` : ''}
                      <strong>${money(final)}</strong>
                      ${pct > 0 ? `<em>(${pct}% OFF)</em>` : ''}
                    `
                }
              </aside>
            </article>
          `;
        }).join('')
      }
    </section>
  `;
}

function offerName(offer) {
  return String(
    [
      offer.title,
      offer.product_title,
      offer.offer_product_title,
      offer.name,
      offer.subtitle
    ].find(
      value =>
        String(value ?? '').trim() &&
        String(value).trim() !== '0'
    ) ||
    'Recommended product'
  );
}

function offerSection() {
  if (customerCartEmpty() || !S.offers.length) {
    return '';
  }

  const list = S.offers
    .filter(offerAllowed)
    .slice(0, 8);

  if (!list.length) return '';

  return `
    <section class="msc-sec" id="msc-live-offers">
      <div class="sectitle">
        <div>
          <h4>
            ${esc(S.cfg?.settings?.upsell_heading || 'Complete your order')}
          </h4>
          <small>Recommended for your cart</small>
        </div>

        <span class="scrollhint">SCROLL</span>
      </div>

      <div class="offerlist">
        ${
          list.map(offer => `
            <article class="offerrow">
              ${
                offer.image_url
                  ? `
                    <img
                      src="${esc(offer.image_url)}"
                      alt=""
                      loading="lazy"
                      decoding="async"
                    >
                  `
                  : ''
              }

              <div class="offerinfo">
                <b>${esc(offerName(offer))}</b>

                ${
                  offer.subtitle &&
                  String(offer.subtitle) !== '0'
                    ? `<small>${esc(offer.subtitle)}</small>`
                    : ''
                }

                <strong>${money(offer.price_cents)}</strong>
              </div>

              <button
                type="button"
                data-o="${esc(offer.id)}"
                data-v="${esc(offer.variant_id)}"
              >
                + ADD
              </button>
            </article>
          `).join('')
        }
      </div>
    </section>
  `;
}

function coupons() {
  if (
    customerCartEmpty() ||
    S.cfg?.settings?.enable_coupon == 0
  ) {
    return '';
  }

  const available = (
    S.cfg?.coupons || []
  ).filter(
    coupon =>
      qualifyingCartTotal() >=
      Number(coupon.min_cart_cents || 0)
  );

  const applied = couponCodeFromCart();

  return `
    <section class="msc-sec couponsec">
      <div class="sectitle">
        <div>
          <h4>
            ${esc(S.cfg?.settings?.coupon_heading || 'Best offers for you')}
          </h4>
          <small>Apply the best available saving</small>
        </div>

        <span class="pct">%</span>
      </div>

      <div class="couponbox ${applied ? 'has-applied' : ''}">
        ${
          applied
            ? `
              <div class="appliedcoupon">
                <span>
                  <i>✓</i>
                  <div>
                    <b>${esc(applied)}</b>
                    <small>Coupon applied successfully</small>
                  </div>
                </span>

                <button type="button" id="msc-remove-code">
                  REMOVE
                </button>
              </div>
            `
            : `
              <div class="couponinput">
                <input
                  id="msc-code"
                  placeholder="${esc(S.cfg?.settings?.coupon_placeholder || 'Enter coupon code')}"
                  autocomplete="off"
                >

                <button type="button" id="msc-apply">
                  APPLY
                </button>
              </div>
            `
        }

        <div id="msc-msg"></div>

        ${
          !applied
            ? available.map(coupon => `
                <button
                  type="button"
                  class="ticket"
                  data-code="${esc(coupon.discount_code)}"
                >
                  <span>
                    <b>${esc(coupon.discount_code)}</b>
                    <small>
                      ${esc(coupon.description || coupon.title)}
                    </small>
                  </span>

                  <em>APPLY</em>
                </button>
              `).join('')
            : ''
        }
      </div>
    </section>
  `;
}

function trust() {
  if (
    customerCartEmpty() ||
    Number(S.cfg?.settings?.show_trust_badges ?? 1) === 0
  ) {
    return '';
  }

  const settings = S.cfg?.settings || {};

  return `
    <div class="trust">
      <span>
        🚚
        <small>
          ${esc(settings.trust_1_label || 'Fast delivery')}
        </small>
      </span>

      <span>
        🔒
        <small>
          ${esc(settings.trust_2_label || 'Secure')}
        </small>
      </span>

      <span>
        ✓
        <small>
          ${esc(settings.trust_3_label || 'Easy support')}
        </small>
      </span>
    </div>
  `;
}

function foot() {
  if (customerCartEmpty()) return '';

  const total = Number(
    S.cart.total_price || 0
  );

  const discount = currentCouponDiscount();
  const code = couponCodeFromCart();
  const subtotal = total + discount;

  const label =
    S.cfg?.settings?.checkout_button_text ||
    'CHECKOUT';

  return `
    <div class="msc-order-summary">
      <div class="msc-summary-row">
        <span>Subtotal</span>
        <strong>${money(subtotal)}</strong>
      </div>

      ${
        discount > 0
          ? `
            <div class="msc-summary-row msc-summary-saving">
              <span>
                ${
                  code
                    ? `Coupon <b>${esc(code)}</b>`
                    : 'Coupon discount'
                }
              </span>
              <strong>− ${money(discount)}</strong>
            </div>
          `
          : ''
      }

      <div class="msc-summary-divider"></div>

      <div class="msc-summary-row msc-summary-total">
        <span>Total</span>
        <strong>${money(total)}</strong>
      </div>

      ${
        discount > 0
          ? `
            <div class="msc-summary-saved">
              ✓ You saved ${money(discount)}
              ${code ? ` with ${esc(code)}` : ''}
            </div>
          `
          : ''
      }
    </div>

    <form
      action="${shop('cart')}"
      method="post"
      id="msc-checkout-form"
    >
      <button
        class="checkout msc-clean-checkout"
        name="checkout"
        value="checkout"
        data-msc-checkout="1"
      >
        <span>${esc(label)}</span>
        <strong>${money(total)}</strong>
        <i>→</i>
      </button>
    </form>

    <small class="secure">
      Secure checkout • UPI • Cards • COD
    </small>
  `;
}

/* ========================= RENDER ========================= */

function render() {
  const footer = document.getElementById('msc-foot');

  if (
    !document.getElementById('msc-body') ||
    !footer
  ) {
    return;
  }

  syncThemeCartCount({
    emitEvents: false
  });

  patchPart('progress', progress());
  patchPart('items', items());
  patchPart('coupons', coupons());
  patchPart('trust', trust());
  patchNode(footer, foot());

  bind();
}

function renderOffers() {
  if (customerCartEmpty()) {
    patchPart('offers', '');
    return;
  }

  patchPart('offers', offerSection());
  bind();
}

function renderFast() {
  render();
  scheduleOffers(70);
}

/* ========================= NOTICE / CELEBRATION ========================= */

function notice(message, type = 'error') {
  const node = document.querySelector(
    '#msc-body [data-msc-part="notice"]'
  );

  if (!node) return;

  clearTimeout(S.noticeTimer);

  node.className = `notice ${type}`;
  node.textContent = message;
  node.__mscHtml = undefined;

  S.noticeTimer = setTimeout(() => {
    node.textContent = '';
    node.className = '';
  }, 3500);
}

function celebrate(anchor) {
  try {
    navigator.vibrate?.([30, 30, 30]);
  } catch {}

  const rect = anchor?.getBoundingClientRect();

  const symbols = [
    '✦', '✓', '💜', '✨', '🎉',
    '✦', '✨', '💜', '✓', '🎉',
    '✦', '✨', '💜', '🎉', '✓',
    '✦', '✨', '🎉', '💜', '✦'
  ];

  for (
    let i = 0;
    i < symbols.length;
    i++
  ) {
    const element = document.createElement('i');

    element.className = 'msc-pop';
    element.textContent = symbols[i];

    element.style.left = `${
      rect?.left + rect?.width / 2 ||
      innerWidth - 100
    }px`;

    element.style.top = `${
      rect?.top || 200
    }px`;

    element.style.setProperty(
      '--x',
      `${(Math.random() - 0.5) * 230}px`
    );

    element.style.setProperty(
      '--y',
      `${-35 - Math.random() * 145}px`
    );

    document.body.appendChild(element);

    setTimeout(
      () => element.remove(),
      900
    );
  }
}

/* ========================= QUANTITY ========================= */

function applyOptimisticQuantity(item, newQty) {
  const oldQty = Math.max(
    1,
    Number(item.quantity) || 1
  );

  const oldFinal = Number(
    item.final_line_price ??
    item.line_price ??
    0
  );

  const oldOriginal = Number(
    item.original_line_price ??
    oldFinal
  );

  const finalUnit = oldFinal / oldQty;
  const originalUnit = oldOriginal / oldQty;

  const newFinal = Math.round(
    finalUnit * newQty
  );

  const newOriginal = Math.round(
    originalUnit * newQty
  );

  const delta =
    newFinal - oldFinal;

  if (S.cart) {
    S.cart.total_price = Math.max(
      0,
      Number(S.cart.total_price || 0) + delta
    );

    if (S.cart.items_subtotal_price != null) {
      S.cart.items_subtotal_price = Math.max(
        0,
        Number(S.cart.items_subtotal_price) + delta
      );
    }
  }

  if (newQty <= 0) {
    if (S.cart) {
      S.cart.items = (
        S.cart.items || []
      ).filter(
        line => line !== item
      );
    }
  } else {
    item.quantity = newQty;
    item.final_line_price = newFinal;
    item.line_price = newFinal;
    item.original_line_price = newOriginal;
  }

  if (S.cart) {
    S.cart.item_count = (
      S.cart.items || []
    ).reduce(
      (sum, line) =>
        sum + Number(line.quantity || 0),
      0
    );
  }

  syncThemeCartCount();
}

function resetPendingLineEdits() {
  Object.values(
    S.lineDebounceTimers
  ).forEach(clearTimeout);

  S.lineDebounceTimers = {};
  S.linePending = {};
}

async function flushPendingLineEdits() {
  const entries = Object.entries(
    S.linePending || {}
  );

  if (!entries.length) return;

  const jobs = [];

  for (const [key, pending] of entries) {
    clearTimeout(
      S.lineDebounceTimers[key]
    );

    delete S.lineDebounceTimers[key];

    jobs.push(
      commitQuantityChange(
        key,
        Math.max(
          0,
          Number(pending?.targetQty ?? 0)
        )
      )
    );
  }

  await Promise.allSettled(jobs);
}

function requestQuantityChange(key, quantity) {
  key = String(key || '');

  quantity = Math.max(
    0,
    Number(quantity) || 0
  );

  if (!key) return;

  const item = cartLineByKey(key);

  if (
    !item ||
    isGiftLine(item)
  ) {
    return;
  }

  if (!S.linePending[key]) {
    S.linePending[key] = {
      baseQty: Number(item.quantity || 0),
      variantId: Number(item.variant_id || 0),
      targetQty: quantity
    };
  } else {
    S.linePending[key].targetQty = quantity;
  }

  applyOptimisticQuantity(
    item,
    quantity
  );

  render();

  clearTimeout(
    S.lineDebounceTimers[key]
  );

  const delay =
    customerCartEmpty()
      ? 0
      : 260;

  S.lineDebounceTimers[key] = setTimeout(
    () =>
      commitQuantityChange(
        key,
        quantity
      ),
    delay
  );
}

async function commitQuantityChange(key, quantity) {
  const pending = S.linePending[key] || {};

  delete S.linePending[key];
  delete S.lineDebounceTimers[key];

  try {
    await enqueueMutation(async () => {
      await changeLineRaw(
        key,
        quantity
      );

      const base = Number(
        pending.baseQty || 0
      );

      const eventName =
        quantity === 0
          ? 'matri_cart_item_removed'
          : quantity > base
            ? 'matri_quantity_increased'
            : 'matri_quantity_decreased';

      track(eventName, {
        variant_id:
          pending.variantId || null,

        value_cents:
          Number(S.cart?.total_price || 0),

        metadata: {
          old_quantity: base,
          new_quantity: quantity
        }
      });
    });

    if (S.linePending[key]) {
      return;
    }

    await syncFreeGifts();

    syncThemeCartCount();
    render();
    scheduleOffers(80);

  } catch (error) {
    if (S.linePending[key]) {
      return;
    }

    await cart(true).catch(() => {});

    syncThemeCartCount();
    render();
    scheduleOffers(80);

    notice(
      error?.message ||
      'Could not update cart.'
    );
  }
}

/* ========================= ADD OFFER ========================= */

async function addOffer(variantId, offerId, button) {
  const old =
    button?.textContent ||
    '+ ADD';

  if (button) {
    button.disabled = true;
    button.textContent = '…';
  }

  try {
    await enqueueMutation(async () => {
      await req(shop('cart/add.js'), {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: {
              _matri_offer_id: String(offerId)
            }
          }]
        })
      });

      await cart(true);
    });

    const offer = S.offers.find(
      item =>
        Number(item.id) === Number(offerId)
    );

    track('matri_upsell_added', {
      variant_id: Number(variantId),
      offer_id: Number(offerId),
      value_cents: Number(offer?.price_cents || 0)
    });

    await syncFreeGifts();

    syncThemeCartCount();
    render();
    scheduleOffers(50);

  } catch (error) {
    if (button) {
      button.disabled = false;
      button.textContent = old;
    }

    notice(
      error?.message ||
      'Could not add product.'
    );
  }
}

/* ========================= COUPON ENGINE ========================= */

async function applyDiscountWithAjax(code) {
  S.couponMutationUntil = Date.now() + 1800;

  const response = await req(shop('cart/update.js'), {
    method: 'POST',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify({
      discount: code
    })
  });

  adoptCart(response);

  if (currentCouponApplication(code)) {
    return S.cart;
  }

  await sleep(55);
  await cart(true);

  return S.cart;
}

async function applyDiscountWithSessionRoute(code) {
  S.couponMutationUntil = Date.now() + 1800;

  const route =
    shop(`discount/${encodeURIComponent(code)}`) +
    `?redirect=${encodeURIComponent(shop('cart'))}&msc=${Date.now()}`;

  const response = await RAW_FETCH(route, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'follow',
    headers: {
      'Cache-Control': 'no-cache'
    }
  });

  if (!response.ok) {
    throw Error(
      `Shopify discount route returned HTTP ${response.status}.`
    );
  }

  await sleep(90);
  await cart(true);
}

function setCouponButtonBusy(button, busy = true) {
  if (!button) return;

  button.disabled = busy;

  button.classList.toggle(
    'msc-coupon-busy',
    busy
  );

  if (
    button.classList.contains('ticket')
  ) {
    const label = button.querySelector('em');

    if (label) {
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel =
          label.textContent.trim() ||
          'APPLY';
      }

      label.textContent =
        busy
          ? 'APPLYING…'
          : button.dataset.originalLabel;
    }

    return;
  }

  if (!button.dataset.originalLabel) {
    button.dataset.originalLabel =
      button.textContent.trim() ||
      'APPLY';
  }

  button.textContent =
    busy
      ? 'APPLYING…'
      : button.dataset.originalLabel;
}

async function applyCoupon(code, button) {
  code = String(code || '')
    .trim()
    .toUpperCase();

  if (!code) {
    notice('Enter a coupon code.');
    return false;
  }

  await flushPendingLineEdits();

  setCouponButtonBusy(
    button,
    true
  );

  return enqueueMutation(async () => {
    let before = 0;
    let after = 0;
    let confirmed = 0;
    let application = null;
    let ajaxError = null;

    try {
      await cart(false);

      before = Number(
        S.cart?.total_price || 0
      );

      S.appliedCode = '';
      S.couponSavings = 0;
      S.couponMutationUntil = Date.now() + 2000;

      try {
        await applyDiscountWithAjax(code);
      } catch (error) {
        ajaxError = error;

        console.warn(
          'Matri Smart Cart: Ajax coupon apply failed; trying session route.',
          error
        );
      }

      application = currentCouponApplication(code);

      after = Number(
        S.cart?.total_price || 0
      );

      let measured = Math.max(
        0,
        before - after
      );

      let allocated =
        couponDiscountFromAllocations(code);

      let direct =
        directCouponApplicationAmount(application);

      if (
        !application &&
        measured <= 0 &&
        allocated <= 0 &&
        direct <= 0
      ) {
        await applyDiscountWithSessionRoute(code);

        application =
          currentCouponApplication(code);

        after = Number(
          S.cart?.total_price || 0
        );

        measured = Math.max(
          0,
          before - after
        );

        allocated =
          couponDiscountFromAllocations(code);

        direct =
          directCouponApplicationAmount(application);
      }

      confirmed = Math.max(
        measured,
        allocated,
        direct
      );

      if (
        !application &&
        confirmed <= 0
      ) {
        throw Error(
          `Coupon ${code} is invalid or not applicable to this cart.${
            ajaxError?.message &&
            !/please try again/i.test(ajaxError.message)
              ? ` (${ajaxError.message})`
              : ''
          }`
        );
      }

      S.appliedCode = code;
      S.couponSavings = confirmed;
      S.couponMutationUntil = 0;

      syncCouponStateFromCart();

      if (!S.appliedCode) {
        S.appliedCode = code;
      }

      if (Number(S.couponSavings || 0) <= 0) {
        S.couponSavings = confirmed;
      }

      /*
       * Direct stable gift reconciliation.
       * Do NOT enqueue from inside syncFreeGifts().
       */
      await syncFreeGifts();

      syncThemeCartCount({
        emitEvents: false
      });

      render();
      scheduleOffers(45);

      track('matri_coupon_applied', {
        coupon_code: S.appliedCode,
        value_cents: Number(
          S.cart?.total_price || after
        ),
        metadata: {
          before_cents: before,
          after_cents: Number(
            S.cart?.total_price || after
          ),
          savings_cents: confirmed,
          shopify_application_found: !!application,
          fallback_used: !!ajaxError
        }
      });

      notice(
        confirmed > 0
          ? `${S.appliedCode} applied. You saved ${money(confirmed)}.`
          : `${S.appliedCode} applied successfully.`,
        'success'
      );

      const box = document.querySelector('.couponbox');

      if (box) {
        box.classList.add(
          'coupon-success-flash'
        );

        setTimeout(
          () =>
            box.classList.remove(
              'coupon-success-flash'
            ),
          700
        );
      }

      requestAnimationFrame(() => {
        celebrate(
          document.querySelector('.appliedcoupon') ||
          box
        );
      });

      return true;

    } catch (error) {
      S.appliedCode = '';
      S.couponSavings = 0;
      S.couponMutationUntil = 0;

      await cart(true).catch(() => {});

      renderFast();

      notice(
        error?.message ||
        'Coupon could not be applied.'
      );

      return false;

    } finally {
      if (
        button &&
        document.body.contains(button)
      ) {
        setCouponButtonBusy(
          button,
          false
        );
      }
    }
  });
}

async function clearDiscountViaAjax() {
  S.couponMutationUntil = Date.now() + 1800;

  const response = await req(shop('cart/update.js'), {
    method: 'POST',
    cache: 'no-store',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    },
    body: JSON.stringify({
      discount: ''
    })
  });

  adoptCart(response);
}

async function removeDiscountRobustly(
  removedCode = '',
  expectedSavings = 0,
  beforeTotal = 0
) {
  S.couponMutationUntil = Date.now() + 2500;

  await clearDiscountViaAjax();

  await sleep(60);
  await cart(true);

  let stillApplied =
    removedCode
      ? cartHasCouponCode(removedCode)
      : !!currentCouponApplication('');

  if (stillApplied) {
    await sleep(120);

    await clearDiscountViaAjax();

    await sleep(80);
    await cart(true);

    stillApplied =
      removedCode
        ? cartHasCouponCode(removedCode)
        : !!currentCouponApplication('');
  }

  if (
    !stillApplied &&
    Number(expectedSavings || 0) > 0 &&
    Number(beforeTotal || 0) > 0
  ) {
    const current = Number(
      S.cart?.total_price || 0
    );

    const expectedMinimum =
      Number(beforeTotal) +
      Math.max(
        1,
        Number(expectedSavings) - 1
      );

    if (current < expectedMinimum) {
      await sleep(100);
      await cart(true);
    }
  }

  if (stillApplied) {
    throw Error(
      removedCode
        ? `Coupon ${removedCode} could not be removed. Please try again.`
        : 'Coupon could not be removed. Please try again.'
    );
  }

  S.couponMutationUntil = 0;
  S.appliedCode = '';
  S.couponSavings = 0;
}

async function clearCoupon() {
  resetPendingLineEdits();

  return enqueueMutation(async () => {
    try {
      await cart(true);

      const removedCode =
        couponCodeFromCart() ||
        S.appliedCode;

      const expectedSavings =
        currentCouponDiscount();

      const beforeTotal = Number(
        S.cart?.total_price || 0
      );

      await removeDiscountRobustly(
        removedCode,
        expectedSavings,
        beforeTotal
      );

      S.appliedCode = '';
      S.couponSavings = 0;

      syncCouponStateFromCart();

      await syncFreeGifts();

      syncThemeCartCount({
        emitEvents: false
      });

      render();
      scheduleOffers(45);

      track('matri_coupon_removed', {
        coupon_code: removedCode,
        value_cents: Number(
          S.cart?.total_price || 0
        ),
        metadata: {
          previous_savings_cents:
            Number(expectedSavings || 0),
          verified_removed:
            !cartHasCouponCode(removedCode)
        }
      });

      notice(
        removedCode
          ? `${removedCode} removed.`
          : 'Coupon removed.',
        'success'
      );

    } catch (error) {
      S.couponMutationUntil = 0;

      await cart(true).catch(() => {});

      render();

      notice(
        error?.message ||
        'Could not remove coupon.'
      );
    }
  });
}

/* ========================= BIND ========================= */

function bind() {
  document.querySelectorAll(
    '[data-key][data-q]'
  ).forEach(button => {
    button.onclick = () =>
      requestQuantityChange(
        button.dataset.key,
        Number(button.dataset.q)
      );
  });

  document.querySelectorAll(
    '[data-o]'
  ).forEach(button => {
    button.onclick = () =>
      addOffer(
        Number(button.dataset.v),
        Number(button.dataset.o),
        button
      );
  });

  document.querySelectorAll(
    '[data-code]'
  ).forEach(button => {
    button.onclick = () =>
      applyCoupon(
        button.dataset.code,
        button
      );
  });

  const apply = document.getElementById('msc-apply');

  if (apply) {
    apply.onclick = () =>
      applyCoupon(
        document.getElementById('msc-code')?.value,
        apply
      );
  }

  const remove = document.getElementById(
    'msc-remove-code'
  );

  if (remove) {
    remove.onclick = clearCoupon;
  }

  const form = document.getElementById(
    'msc-checkout-form'
  );

  if (form) {
    form.onsubmit = event => {
      if (S.checkoutReplay) {
        trackCheckoutIntent(
          'smart_cart_form_submit'
        );

        return true;
      }

      if (checkoutNeedsGuard()) {
        event?.preventDefault?.();
        guardedCheckoutReplay();
        return false;
      }

      trackCheckoutIntent(
        'smart_cart_form_submit'
      );

      return true;
    };
  }

  const continueButton = document.getElementById(
    'msc-continue'
  );

  if (continueButton) {
    continueButton.onclick = close;
  }
}

/* ========================= REFRESH / EXTERNAL ========================= */

async function refresh({
  forceConfig = false,
  reconcileGifts = true
} = {}) {
  resetPendingLineEdits();

  await Promise.all([
    cart(true),
    cfg(forceConfig)
  ]);

  if (reconcileGifts) {
    await syncFreeGifts();
  }

  syncThemeCartCount();
  render();
  scheduleOffers(50);

  return S.cart;
}

function scheduleExternalCartSync(delay = 80) {
  clearTimeout(S.externalSyncTimer);

  S.externalSyncTimer = setTimeout(async () => {
    try {
      resetPendingLineEdits();

      await cart(true);
      await cfg();
      await syncFreeGifts();

      syncThemeCartCount();

      render();
      scheduleOffers(50);

    } catch (error) {
      console.warn(
        'Matri Smart Cart external cart sync failed:',
        error
      );
    }
  }, delay);
}

function installCartMutationObserver() {
  if (
    window.__MSC_CART_FETCH_OBSERVER_124
  ) {
    return;
  }

  window.__MSC_CART_FETCH_OBSERVER_124 = 1;

  window.fetch = async function(...args) {
    const input = args[0];

    const url =
      typeof input === 'string'
        ? input
        : (input?.url || '');

    const response = await RAW_FETCH(...args);

    try {
      const pathname = new URL(
        url,
        location.origin
      ).pathname;

      if (
        /\/cart\/(add|change|update|clear)\.js$/.test(
          pathname
        )
      ) {
        scheduleExternalCartSync(65);
      }
    } catch {}

    return response;
  };

  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'product:added',
    'ajaxProduct:added'
  ].forEach(name => {
    document.addEventListener(name, event => {
      if (
        event?.detail?.source ===
        'matri-smart-cart'
      ) {
        return;
      }

      scheduleExternalCartSync(55);
    });
  });
}

/* ========================= CHECKOUT SAFETY ========================= */

function checkoutTargetFromEvent(event) {
  const target = event?.target;

  if (!(target instanceof Element)) {
    return null;
  }

  if (!target.closest('#msc-drawer')) {
    return null;
  }

  const candidate = target.closest(
    'button, a[href], input[type="submit"], [role="button"]'
  );

  if (!candidate) {
    return null;
  }

  if (
    candidate.matches(
      '#msc-checkout-form button[name="checkout"],' +
      '#msc-checkout-form [data-msc-checkout],' +
      '.msc-clean-checkout,' +
      '[name="checkout"],' +
      '[data-msc-checkout]'
    )
  ) {
    return candidate;
  }

  const identity = [
    candidate.id,
    candidate.className,
    candidate.getAttribute('name'),
    candidate.getAttribute('data-action'),
    candidate.getAttribute('data-checkout'),
    candidate.textContent
  ]
    .map(
      value =>
        String(value || '').toLowerCase()
    )
    .join(' ');

  if (
    identity.includes('checkout') ||
    identity.includes('magic checkout')
  ) {
    return candidate;
  }

  if (candidate instanceof HTMLAnchorElement) {
    try {
      const url = new URL(
        candidate.href,
        location.origin
      );

      if (
        /\/checkout(?:\/|$)/i.test(
          url.pathname
        )
      ) {
        return candidate;
      }
    } catch {}
  }

  return null;
}

function giftStateNeedsReconcile() {
  const desired = desiredGiftRules();
  const marked = markedGiftLines();

  if (!desired.length) {
    return marked.length > 0;
  }

  if (desired.length !== 1) {
    return true;
  }

  if (marked.length !== 1) {
    return true;
  }

  const rule = desired[0];
  const line = marked[0];

  if (
    String(line.properties?._matri_free_gift || '') !==
    String(rule.id)
  ) {
    return true;
  }

  if (
    Number(line.variant_id || 0) !==
    Number(rule.gift_variant_id || 0)
  ) {
    return true;
  }

  if (Number(line.quantity || 0) !== 1) {
    return true;
  }

  if (!giftLineIsFree(line)) {
    return true;
  }

  return false;
}

function checkoutNeedsGuard() {
  const couponMutating =
    Date.now() <
    Number(S.couponMutationUntil || 0);

  return (
    !hasPaidMerchandise() ||
    pendingLineMutationCount() > 0 ||
    !!S.cartP ||
    !!S.giftP ||
    couponMutating ||
    giftStateNeedsReconcile()
  );
}

async function ensureCheckoutReady() {
  if (S.checkoutGuardBusy) {
    return false;
  }

  S.checkoutGuardBusy = true;

  try {
    await flushPendingLineEdits();

    await S.mutationChain.catch(() => {});

    await cart(true);
    await cfg();

    await syncFreeGifts();

    await cart(true);

    if (!hasPaidMerchandise()) {
      if (markedGiftLines().length) {
        await syncFreeGifts();
        await cart(true);
      }

      syncThemeCartCount({
        emitEvents: false
      });

      render();

      notice(
        'Your bag is empty. Add a product before checkout.'
      );

      return false;
    }

    if (giftStateNeedsReconcile()) {
      await syncFreeGifts();
      await cart(true);

      if (giftStateNeedsReconcile()) {
        console.warn(
          'Matri Smart Cart blocked checkout because gift state was unstable.'
        );

        syncThemeCartCount({
          emitEvents: false
        });

        render();

        notice(
          'Cart is still updating. Please try checkout again.'
        );

        return false;
      }
    }

    syncThemeCartCount({
      emitEvents: false
    });

    render();

    return true;

  } catch (error) {
    console.warn(
      'Matri Smart Cart checkout guard failed:',
      error
    );

    await cart(true).catch(() => {});

    syncThemeCartCount({
      emitEvents: false
    });

    render();

    notice(
      'Cart is updating. Please try checkout again.'
    );

    return false;

  } finally {
    S.checkoutGuardBusy = false;
  }
}

async function guardedCheckoutReplay() {
  const ready = await ensureCheckoutReady();

  if (!ready) return;

  const button = document.querySelector(
    '#msc-checkout-form button[name="checkout"], #msc-drawer [data-msc-checkout]'
  );

  if (!button) return;

  S.checkoutReplay = true;

  try {
    trackCheckoutIntent(
      'smart_cart_guarded_replay'
    );

    button.click();

  } finally {
    setTimeout(() => {
      S.checkoutReplay = false;
    }, 0);
  }
}

document.addEventListener(
  'pointerdown',
  event => {
    const target =
      checkoutTargetFromEvent(event);

    if (
      !target ||
      S.checkoutReplay
    ) {
      return;
    }

    if (checkoutNeedsGuard()) {
      event.preventDefault();
      event.stopImmediatePropagation();

      guardedCheckoutReplay();

      return;
    }

    trackCheckoutIntent(
      'smart_cart_pointerdown'
    );
  },
  true
);

document.addEventListener(
  'click',
  event => {
    const target =
      checkoutTargetFromEvent(event);

    if (
      !target ||
      S.checkoutReplay
    ) {
      return;
    }

    if (checkoutNeedsGuard()) {
      event.preventDefault();
      event.stopImmediatePropagation();

      guardedCheckoutReplay();

      return;
    }

    trackCheckoutIntent(
      'smart_cart_click_capture'
    );
  },
  true
);

/* ========================= OPEN / CLOSE ========================= */

async function open() {
  if (S.opening) {
    /* Drawer is already opening; keep the UI responsive. */
    document.getElementById('msc-overlay')?.classList.add('open');
    document.getElementById('msc-drawer')?.classList.add('open');
    return;
  }

  S.opening = true;
  const token = ++S.openingToken;

  const overlay = document.getElementById('msc-overlay');
  const drawer = document.getElementById('msc-drawer');
  const scroll = document.querySelector('.msc-scroll');

  if (scroll) scroll.scrollTop = 0;

  /* Open on the very first frame. No network request blocks animation. */
  overlay?.classList.add('open');
  drawer?.classList.add('open');
  drawer?.setAttribute('aria-hidden', 'false');
  document.documentElement.style.overflow = 'hidden';

  resetPendingLineEdits();

  /*
   * Hydrate a short-lived session snapshot if this page has not yet
   * finished preloading. It is visual-only and immediately revalidated.
   */
  if (!S.cart) {
    hydrateCartSnapshot();
  }

  if (S.cart) {
    syncThemeCartCount({ emitEvents: false });
    render();

    if (S.offers?.length) {
      renderOffers();
    }
  }

  try {
    /*
     * If eager preload just completed, cart(false) resolves immediately.
     * Otherwise it performs one authoritative background cart read.
     */
    await Promise.all([
      cart(false),
      cfg()
    ]);

    if (token !== S.openingToken) return;

    /* Show fresh Shopify data BEFORE gift verification. */
    syncThemeCartCount({ emitEvents: false });
    render();

    /* Usually zero-network when the gift state is already correct. */
    const giftChanged = await syncFreeGifts();

    if (token !== S.openingToken) return;

    if (giftChanged) {
      syncThemeCartCount({ emitEvents: false });
      render();
    }

    scheduleOffers(20);

    track('matri_cart_opened', {
      value_cents: Number(S.cart?.total_price || 0),
      metadata: {
        item_count: paidItemCount(),
        qualifying_cart_cents: qualifyingCartTotal(),
        instant_render: true
      }
    });

  } catch (error) {
    console.warn('Matri Smart Cart open failed:', error);

    if (S.cart) {
      renderFast();

      if (Number(error?.status || 0) !== 429) {
        notice(error?.message || 'Could not fully refresh cart.');
      }
    } else if (Number(error?.status || 0) !== 429) {
      notice(error?.message || 'Could not load cart.');
    }
  } finally {
    S.opening = false;
  }
}

function close() {
  syncThemeCartCount();

  document.getElementById('msc-overlay')
    ?.classList
    .remove('open');

  const drawer =
    document.getElementById('msc-drawer');

  drawer?.classList.remove('open');

  drawer?.setAttribute(
    'aria-hidden',
    'true'
  );

  document.documentElement.style.overflow = '';

  track('matri_cart_closed', {
    value_cents: Number(
      S.cart?.total_price || 0
    )
  });

  const scroll =
    document.querySelector('.msc-scroll');

  if (scroll) {
    scroll.scrollTop = 0;
  }
}

/* ========================= CART LINK ========================= */

function isCartLink(element) {
  if (!(element instanceof Element)) {
    return false;
  }

  const anchor = element.closest('a[href]');

  if (!anchor) return false;

  try {
    const url = new URL(
      anchor.href,
      location.origin
    );

    const cartPath = new URL(
      shop('cart'),
      location.origin
    )
      .pathname
      .replace(/\/+$/, '');

    return (
      url.origin === location.origin &&
      url.pathname.replace(/\/+$/, '') === cartPath
    );

  } catch {
    return false;
  }
}

document.addEventListener(
  'click',
  event => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      event.target.closest('[data-matri-cart-open]') ||
      isCartLink(event.target)
    ) {
      event.preventDefault();
      event.stopPropagation();
      open();
    }
  },
  true
);

/* ========================= PRODUCT FORM OBSERVER ========================= */

document.addEventListener(
  'submit',
  event => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const form = event.target.closest(
      'form[action*="/cart/add"]'
    );

    if (!form) return;

    const before = Number(
      S.cart?.item_count || 0
    );

    const check = async attempt => {
      try {
        const updated = await cart(true);

        const count = Number(
          updated?.item_count || 0
        );

        syncThemeCartCount();

        if (count > before) {
          track('matri_cart_item_added', {
            value_cents: Number(
              updated?.total_price || 0
            ),
            metadata: {
              item_count: count,
              previous_item_count: before
            }
          });

          await open();
          return;
        }

      } catch {}

      if (attempt === 0) {
        setTimeout(
          () => check(1),
          320
        );
      }
    };

    setTimeout(
      () => check(0),
      90
    );
  },
  true
);

/* ========================= PRELOAD ========================= */

function warm() {
  /*
   * v12.4: preload immediately instead of waiting for requestIdleCallback.
   * Config is often served from session cache, while cart.js is tiny and
   * gives the drawer everything it needs before the user can click it.
   */
  hydrateCartSnapshot();

  if (S.cart) {
    syncThemeCartCount({ emitEvents: false });
  }

  cfg().catch(() => {});

  cart(true)
    .then(() => {
      syncThemeCartCount({ emitEvents: false });
    })
    .catch(() => {});
}

/* ========================= BOOT ========================= */

shell();
installCartMutationObserver();
warm();

window.MatriSmartCart = {
  open,
  close,
  refresh,
  applyCoupon,
  clearCoupon,
  track,
  syncFreeGifts,
  syncThemeCartCount,
  qualifyingCartTotal,
  desiredGiftRules,
  effectiveMilestones,
  paidItemCount,
  hasPaidMerchandise
};

})();